import { AddOnType, CreateLocationRequest, CreatePointRequest, CreateTopicRequest, DiscoveryLevel, GeocodeType, GetTopicResponse, IntentType, KlavioEventPropertyKey, KlayvioEvents, Location, ObjectId, OnboardingState, SearchIcon, TopicCategory } from '../types';
import { LitElement, css, html } from 'lit';
import { customElement, property, query, state } from 'lit/decorators';
import { dasherize, delay, findIcon, getFirstIcon, getFromLocalStorage, isDefined, undefinedIfEmpty } from '../helpers/utils';
import { detectMobileViewport, getDeviceGeolocation } from '../helpers/device';
import { getRandomColor, makeColorIntoDataSvg } from '../helpers/colors';
import { gptCall, promptToInitializedMapAI } from '../helpers/api/gpt-api';
import { logEventWithKlayvio, logWithAmplitude } from '../helpers/api/api';

import { DEFAULT_CATEGORY_COLORS } from '../helpers/constants';
import { FormInputButton } from './theme/form-input-button';
import { FormSwitch } from './theme/form-switch';
import { Menu } from '@material/mwc-menu';
import { ProxiInputPlace } from './common/proxi-input-place';
import { ProxiTextfield } from './theme/proxi-textfield';
import { StateController } from '../helpers/state/state-management/state-controller';
import { UpgradeDialogElement } from './upgrade-dialog';
import accountInfo from '../helpers/state/account-info';
import { getMainPage } from '../helpers/page-name';
import { ifDefined } from 'lit/directives/if-defined';
import organizationInfo from '../helpers/state/organization-info';
import pointCache from '../helpers/state/point-cache';
import { saveEmail } from '../helpers/api/account-api';
import { smoothViewport } from '../helpers/api/google-api';
import subscriptionInfo from '../helpers/state/subscription-info';
import { suggestPointsOnboarding } from '../helpers/api/points-api';
import topicData from '../helpers/state/topic-data';
import { updateTopicAndPointsWithEmail } from '../helpers/api/topics-api';
import { validateEmail } from '../helpers/strings';

@customElement('onboarding-modal')
export class OnboardingModal extends LitElement {
    amplitude_callsite: string = 'Onboarding Modal';



    // @query('#defaultcenter')
    // private defaultCenterElement!: any | ProxiTextfield;

    @query('#suggestPoints')
    private readonly suggestPointsElement!: FormSwitch;

    @query('#focusArea')
    private readonly focusAreaElement!: ProxiInputPlace;

    @query('#iconMenu')
    iconMenu!: Menu;

    @query('#iconMenuHolder')
    iconMenuHolder!: HTMLDivElement;

    @property({ type: String })
    userLocation: string = '';

    @query('#emailAddress')
    emailAddressElement!: FormInputButton;

    @query('#gptInitializePrompt')
    gptInitializePromptElement?: ProxiTextfield;

    @query('#upgradeDialogInnerComponent')
    upgradeDialogInnerComponent!: UpgradeDialogElement;

    @query('#suggestionElement')
    suggestionElement?: ProxiTextfield;

    @property({ type: String })
    email: string = accountInfo.email ?? '';

    @state()
    onboardingStep?: OnboardingState = undefined;

    @state()
    selectedIntent?: IntentType = IntentType.Map;

    @state()
    useGPT: boolean = false;

    @state()
    waitingForGPT: boolean = false;

    @state()
    suggestedPrompts: string[] = ['Wedding Weekend near Austin', 'Small Business Saturday Guide in Sarasota', 'Relocation Guide in Houston', 'Real Estate Availability and Surrounding Attractions in Tampa Bay, FL', 'Christmas Lights Tour in Bryan', 'Local Business Map for Chamber of Commerce in Sarasota', 'Kid Friendly Places in Seattle for My Blog', 'Coziest Restaurants in Austin for My Blog'];

    @state()
    suggestPoints: boolean = false;

    @state()
    loadingPromptSuggestions: boolean = true;

    @state()
    categorySuggestionsLoading: boolean = false;

    @state()
    suggestedPointsView: boolean = false;

    @state()
    suggestedIcons: SearchIcon[] = [];

    @state()
    suggestedPoints: CreatePointRequest[] = [];

    @state()
    deletedPoints: CreatePointRequest[] = [];

    @state()
    loadingPointSuggestions: boolean = false;

    @state()
    loadingPointsToMap: boolean = false;

    @state()
    city?: string;

    @state()
    iconSearchCategory?: TopicCategory;

    @state()
    suggestedMapCenter?: CreateLocationRequest;

    @state()
    showSuggestedCenter: boolean = false;

    @state()
    sectionHeader: string = '';

    @state()
    inspirationMaps: GetTopicResponse[] = [];

    @state()
    viewport?: google.maps.LatLngBounds;

    @state()
    placeholder: string = 'Small Business Guide in Seattle';

    @state()
    promptFocused: boolean = false;

    @state()
    isTemplate: boolean = false;

    @state()
    newCenter: boolean = false;

    get addMoreSuggestions() {
        return ['Kid Friendly Places', 'Places That Will Impress My In-Laws', 'Sunny Day Activities', 'Touristy Spots', 'Places to Chill', 'Where Gen Z Hangs Out'];
    }

    private readonly bindPointCacheState = new StateController(this, pointCache);
    private readonly bindTopicState = new StateController(this, topicData);
    private readonly bindAccountState = new StateController(this, accountInfo);

    gptQuery: string = '';

    suggestion = '';

    intervalId: number = 0;

    addMorecategory?: TopicCategory;

    templateName = 'My Proxi Map';

    async firstUpdated(_changedProperties: any) {
        await this.startup();
    }

    private async startup() {
        if (this.onboardingStep === OnboardingState.Intent) {
            if (topicData.topic?.name !== 'My Proxi Map' && topicData.topic?.name !== 'New Map from Template') {
                this.isTemplate = true;
            }
        }

        if (isDefined(this.focusAreaElement) && topicData.topic?.default_map_center.search !== 'default_map_center') {
            this.focusAreaElement.value = topicData.topic?.default_map_center;
        }

        // handle placeholder changes
        let index = 0;
        this.intervalId = setInterval(() => {
            index = (index + 1) % this.suggestedPrompts.length;
            this.placeholder = this.suggestedPrompts[index];
        }, 5000);

    }

    private selectIntent(intent: IntentType) {
        this.selectedIntent = intent;
    }

    private shouldShowWalkthrough(): boolean {
        return this.selectedIntent !== undefined &&
               this.selectedIntent !== IntentType.Map &&
               this.selectedIntent !== IntentType.Hunt;
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        // Clear the interval when the component unmounts
        if (this.intervalId) {
            clearInterval(this.intervalId);
        }
    }

    protected async updated(changedProperties: Map<string | number | symbol, unknown>): Promise<void> {
        // if (changedProperties.has('useGPT')) {
        //     await this.fixDefaultCenterElement();
        // }
        if (changedProperties.has('onboardingStep') || changedProperties.has('loading')) {
            const somethingLoading = this.loadingPointSuggestions || this.loadingPointsToMap;
            await this.startup();
            this.sectionHeader = somethingLoading ? 'Loading' : this.header;
            this.dispatchEvent(new CustomEvent('on-header-change', { detail: { header: this.header } }));
        }
    }

    private async removeCategory(id?: ObjectId) {
        if (topicData.categories.length <= 1 || id === undefined) {
            return;
        }
        const newCategories = topicData.categories.filter((c) => c._id !== id);
        const changes: Partial<CreateTopicRequest> = {
            categories: newCategories
        };

        await topicData.fetchAndUpdateTopicData(changes, this.amplitude_callsite);
    }

    private async addCategory() {
        // get random color from DEFAULT_CATEGORY_COLORS
        const randomColor = DEFAULT_CATEGORY_COLORS[Math.floor(Math.random() * DEFAULT_CATEGORY_COLORS.length)];

        const newCategory : TopicCategory = {
            name: 'New Category',
            icon: makeColorIntoDataSvg(randomColor),
            _id: undefined,
            icon_size: undefined,
            can_crowdsource: true,
        };

        const categories = [...topicData.categories, newCategory];
        const changes: Partial<CreateTopicRequest> = {
            categories
        };
        await topicData.fetchAndUpdateTopicData(changes, this.amplitude_callsite);
    }

    private selectAddMoreCategory(category?: TopicCategory) {
        this.addMorecategory = category;
    }

    private async updateCategoryName(category: TopicCategory, name: string) {
        category.name = name.trim();
        let newCategories = topicData.categories;

        // find category in newCategories and update it
        newCategories = newCategories.map((c) => {
            if (c._id === category._id) {
                return category;
            }
            return c;
        });

        const changes: Partial<CreateTopicRequest> = {
            categories: newCategories
        };
        await topicData.fetchAndUpdateTopicData(changes, this.amplitude_callsite);
    }

    private async getSuggestedIcons(search: string, category: TopicCategory) {
        this.iconSearchCategory = category;
        let searchTerm = search;
        for (let i = 0; i < 3; i++) {
            this.suggestedIcons = await findIcon(
                this.amplitude_callsite,
                searchTerm,
                'stickers,emoji,poly,3d-fluency,officel'
            );
            if (this.suggestedIcons.length > 2) {
                break;
            } else if (i === 0 && searchTerm.endsWith('s')) {
                searchTerm = searchTerm.slice(0, -1);
            } else if (i === 1) {
                const words = searchTerm.split(' ');
                searchTerm = words[0];
            }
        }
    }

    private async updateIcon(icon: SearchIcon) {
        const category = topicData.categories.find(c => c._id === this.iconSearchCategory?._id);
        if (!category) {
            return;
        }
        const icon_string = 'https://img.icons8.com/' + icon.platform + '/' + icon.commonName + '.png';
        category.icon = icon_string;
        // update the topicData.categories
        const changes: Partial<CreateTopicRequest> = {
            categories: topicData.categories
        };
        await topicData.fetchAndUpdateTopicData(changes, this.amplitude_callsite);
        this.suggestedIcons = [];
    }

    private async getMapPromptSuggestions() {
        this.loadingPromptSuggestions = true;
        if (topicData.topic?.default_map_center?.search === 'default_map_center') {
            await this.getUserGeneralLocation();
        } else {
            await this.updateSuggestedMapCenter(topicData.topic?.default_map_center);
        }
        // if user location is not defined, show location
        if (!this.userLocation || !accountInfo.isAuthenticated) {
            this.showSuggestedCenter = true;
            this.loadingPromptSuggestions = false;
            return;
        }

        const today = new Date();
        const userURL = accountInfo.isAuthenticated ? accountInfo.email.split('@')[1] : 'unregistered';

        const templateName = topicData.topic?.name ?? undefined;
        const intentType = isDefined(this.selectedIntent) ? topicData.getIntentTitle(this.selectedIntent) : undefined;
        const intentDescription = isDefined(this.selectedIntent) ? topicData.getIntentDescription(this.selectedIntent) : undefined;

        const prompt = `I'm an onboarding helper for a custom interactive mapping software helping a user start a map.
            ${this.userLocation && this.userLocation.toLowerCase() !== 'undefined' ? `They just logged in from ${this.userLocation}.` : ''}
            Today is ${today.toLocaleDateString()}.
            ${accountInfo.isAuthenticated ? `  The user's domain url is ${userURL}` : ''}.
            ${isDefined(intentType) ? `They selected to create a "${String(intentType ?? '')}". ${String(intentDescription ?? '')}` : ''}
            ${isDefined(templateName) ? `They are creating an interactive map, challenge, guide or best of competition with the theme: ${String(templateName ?? '')}, which will have a variety of categories and elements` : ''}
            Suggest some short prompts on what I might be trying to map
            ${this.isTemplate ? '' : 'for my business, community, local organizations, neighborhoods, vacation spots, or even upcoming events'}.
            A few examples of the response structure are below in the json instructions. Suggest up to 6 prompts, include at least 1 silly one. Dont make the prompts more than 10 words. Consider what kind of events they could be building for in the next 1-3 months.

            Return the response in json format as an array of objects with the key "Prompt" for each suggestion, like this:
            [
                { "Prompt": "Christmas Lights Tour in Bryan Test for Neighbors" },
                { "Prompt": "Local Business Map for My Chamber of Commerce in Sarasota" },
                { "Prompt": "Kid Friendly Activities in Seattle for My Blog" },
                { "Prompt": "Small Businesses Saturday Challenge in Sugar Land" }
            ]
        `;
        let attempts = 0;
        while (attempts < 3) {
            try {
                const response = await gptCall(this.amplitude_callsite, prompt);
                // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
                const cleaned_response = response.substring(response.indexOf('['), response.lastIndexOf(']') + 1);

                const parsedResponse = JSON.parse(cleaned_response);
                this.suggestedPrompts = parsedResponse.map((item: { Prompt: any; }) => item.Prompt);

                if (typeof this.suggestedPrompts[0] === 'object' || this.suggestedPrompts[0].includes('?')) {
                    throw new Error('First element of suggestedPrompts is an object');
                }

                break; // If we reached this point, it means we had a successful attempt, so we break the loop
            } catch (e) {
                attempts++;
                if (attempts >= 3) {
                    console.error('Failed to get map prompt suggestions after 3 attempts');
                }
            }
        }
        this.loadingPromptSuggestions = false;
    }

    private async handlePromptFocus() {
        this.promptFocused = true;
        await this.gptInitializePromptElement?.layout();
        this.gptInitializePromptElement?.focus();
    }

    private async goToSuggestedPoints() {
        this.onboardingStep = OnboardingState.Points;
        this.loadingPointSuggestions = true;
        if (topicData.topic) {
            await pointCache.instantiatePointCache(topicData.topic, topicData.topicKey, false);
        }
        const viewport = pointCache.map?.getBounds();
        if (viewport) {
            const search_viewport = {
                low: {
                    lat: viewport.getSouthWest().lat(),
                    lng: viewport.getSouthWest().lng(),
                },
                high: {
                    lat: viewport.getNorthEast().lat(),
                    lng: viewport.getNorthEast().lng(),
                }
            };
            this.suggestedPoints = await suggestPointsOnboarding(this.amplitude_callsite, search_viewport, topicData.categories.map(c => c.name));
        };
        if (this.suggestedPoints.length === 0 || !topicData.topic?.default_map_center.latLng) {
            getMainPage().errorToastElement.showToast('An error has occurred. Context: Could not get suggested points. Try again later.');
            this.loadingPointSuggestions = false;
            await this.closeOnboardingModal();
        }
        this.loadingPointSuggestions = false;

        // TODO reset zoom to fit
        this.requestUpdate();
    }

    // for getting a center from the user locale or the prompt response
    private async centerVerification(location: string) {
        let dataGeocode;
        try {
            dataGeocode = await this.focusAreaElement.geocode(location);
        } catch (e) {
            console.error('Error geocoding location for map centering: ', e);
            return;
        }
        if (dataGeocode.length === 0 || !dataGeocode[0].formatted_address) {
            console.error('No geocoding results found for location: ', location);
            return;
        }

        const place = {
            search: dataGeocode[0].formatted_address,
            place_id: dataGeocode[0].place_id,
            latLng: {
                lat: dataGeocode[0].geometry.location.lat(),
                lng: dataGeocode[0].geometry.location.lng(),
            }
        };
        const viewport = dataGeocode[0].geometry.viewport;
        await this.updateSuggestedMapCenter(place, viewport);
    }

    // update the map to match the user's set location and lock it in
    private async updateZoomAndCenter(location: CreateLocationRequest) {
        // if the topic location and the suggested location are the same, don't update
        if (
            topicData.topic?.default_map_center.latLng?.lat === location.latLng?.lat &&
            topicData.topic?.default_map_center.latLng?.lng === location.latLng?.lng
        ) {
            return;
        }
        if (location.search && location.place_id && location.latLng) {
            const changes: Partial<CreateTopicRequest> = {
                default_map_center: {
                    search: location.search,
                    place_id: location.place_id,
                    latLng: location.latLng
                },
                default_map_zoom: Math.round(pointCache.map?.getZoom() ?? 10)
            };
            await topicData.fetchAndUpdateTopicData(changes, this.amplitude_callsite);
        }
    }

    private async updateSuggestedMapCenter(place?: CreateLocationRequest, viewport?: google.maps.LatLngBounds) {
        if (!place) {
            return;
        }

        if (
            this.suggestedMapCenter?.search === place.search &&
            this.suggestedMapCenter?.place_id === place.place_id &&
            this.suggestedMapCenter?.latLng?.lat === place.latLng?.lat &&
            this.suggestedMapCenter?.latLng?.lng === place.latLng?.lng
        ) {
            return;
        }

        this.suggestedMapCenter = place;
        this.focusAreaElement.value = this.suggestedMapCenter;
        // if no latLng, return
        if (!this.suggestedMapCenter.latLng || this.suggestedMapCenter.latLng.lat === 0 || this.suggestedMapCenter.latLng.lng === 0) {
            return;
        }
        if (viewport && pointCache.map) {
            await smoothViewport(pointCache.map, viewport);
            this.newCenter = true;
        }

        if (this.suggestedMapCenter.latLng?.lat !== 0 && this.suggestedMapCenter.latLng?.lng !== 0) {
            await this.updateZoomAndCenter(this.suggestedMapCenter);
        }
    }

    private async deleteSuggestedPoint(point: CreatePointRequest) {
        this.suggestedPoints = this.suggestedPoints.filter(p => p.name !== point.name);
        this.deletedPoints.push(point);
        this.requestUpdate();
    }

    private async addPointsToMap() {
        // sometimes AI reponds with emply string for email when user isn't logged in yet - we need to remove
        this.suggestedPoints = this.suggestedPoints.map((point) => {
            if (point.email === '') {
                point.email = undefined;
            }
            return point;
        });

        if (topicData.topic) {
            this.loadingPointsToMap = true;
            await pointCache.fetchAndAddMany(this.suggestedPoints,GeocodeType.Place);

            try {
                // await pointCache.refreshPoints(false);
                const bounds = new google.maps.LatLngBounds();
                for (let i = 0; i < pointCache.points.length; i++) {
                    const point = pointCache.points[i];
                    bounds.extend(point.location.latLng);
                }
                pointCache.map?.fitBounds(bounds);
            } catch (e) {
                console.error('Error adjusting bounde', e);
            }


            this.requestUpdate();

            // Lots of this is taken from hot-points.ts
            const categoryIdMap = new Map<string, TopicCategory>();
            for (const category of topicData.categories ?? []) {
                if (category._id === undefined) {
                    continue;
                }
                categoryIdMap.set(category._id.$oid, category);
            }

        }
        this.loadingPointsToMap = false;
    }

    async addAppropriateIntentChanges(intent: IntentType) {
        switch (intent) {
            case IntentType.Guide: {
                const addon1 = topicData.allAddons.find(a => a.component === AddOnType.Guide);
                await topicData.installAddon(this.amplitude_callsite, addon1, false);
                const addon2 = topicData.allAddons.find(a => a.component === AddOnType.CustomForm);
                await topicData.installAddon(this.amplitude_callsite, addon2, false);
                const name: string = 'New Guide';
                await topicData.fetchAndUpdateTopicData({ name }, this.amplitude_callsite, false, false);
                break;
            }
            case IntentType.Passport: {
                const addon = topicData.allAddons.find(a => a.component === AddOnType.Challenge);
                await topicData.installAddon(this.amplitude_callsite, addon, false);
                const addon2 = topicData.allAddons.find(a => a.component === AddOnType.CustomForm);
                await topicData.installAddon(this.amplitude_callsite, addon2, false);
                const name: string = 'New Passport Challenge';
                await topicData.fetchAndUpdateTopicData({ name }, this.amplitude_callsite, false, false);
                break;
            }
            case IntentType.Event: {
                const addon2 = topicData.allAddons.find(a => a.component === AddOnType.CustomForm);
                await topicData.installAddon(this.amplitude_callsite, addon2, false);
                const changes: Partial<CreateTopicRequest> = {
                    name: 'New Event Map',
                    has_dates: true,
                };
                await topicData.fetchAndUpdateTopicData(changes, this.amplitude_callsite, false, false);
                break;
            }
            case IntentType.BestOf: {
                const addon = topicData.allAddons.find(a => a.component === AddOnType.BestOf);
                await topicData.installAddon(this.amplitude_callsite, addon, false);
                const name: string = 'Best Of Competition';
                await topicData.fetchAndUpdateTopicData({ name }, this.amplitude_callsite, false, false);
                break;
            }
            case IntentType.Hunt: {
                const addon = topicData.allAddons.find(a => a.component === AddOnType.ScavengerHunt);
                await topicData.installAddon(this.amplitude_callsite, addon, false);
                const name: string = 'New Scavenger Hunt';
                await topicData.fetchAndUpdateTopicData({ name }, this.amplitude_callsite, false, false);
                break;
            }
            case IntentType.Map: {
                const addon2 = topicData.allAddons.find(a => a.component === AddOnType.CustomForm);
                await topicData.installAddon(this.amplitude_callsite, addon2, false);
                break;
            }
        }
    }

    private async goToNextStep() {
        logWithAmplitude(this.amplitude_callsite, `Onboarding Step: ${this.onboardingStep ?? ''}`);
        try {
            if (this.onboardingStep === OnboardingState.Intent) {
                if (!isDefined(this.selectedIntent)) {
                    getMainPage().errorToastElement.showToast('Please select what you want to create');
                    return;
                }
                logWithAmplitude(this.amplitude_callsite, `Selected Intent: ${String(this.selectedIntent) ?? ''}`);
                try {
                    await this.addAppropriateIntentChanges(this.selectedIntent);
                } catch (e) {
                    console.error('Error adding intent changes: ', e);
                }
                topicData.intent = this.selectedIntent;
                this.onboardingStep = OnboardingState.Prompt;
                try {
                    await this.getMapPromptSuggestions();
                } catch (e) {
                    console.error('Error getting map prompt suggestions: ', e);
                }
            } else if (this.onboardingStep === OnboardingState.Prompt) {
                if (isDefined(this.emailAddressElement)) {
                    if (!validateEmail(this.emailAddressElement?.value)) {
                        getMainPage().errorToastElement.showToast('Please enter a valid email address');
                        return;
                    }
                    await this.updateEmail();
                    this.email = this.emailAddressElement?.value;
                    await updateTopicAndPointsWithEmail(this.amplitude_callsite, topicData.topicId, this.email, topicData.topicKey, 0);
                }
                if (isDefined(this.gptInitializePromptElement) && this.gptInitializePromptElement.value === '') {
                    getMainPage().errorToastElement.showToast('Please enter a title or theme for your map');
                    return;
                }
                if (this.showSuggestedCenter && !this.suggestedMapCenter) {
                    getMainPage().errorToastElement.showToast('Please enter a location for your map');
                    return;
                }
                if (!this.isTemplate && this.gptInitializePromptElement) {
                    await this.initializeAiTopic(
                        `${this.gptInitializePromptElement.value}, ${this.suggestedMapCenter?.search ?? ''}`
                    );
                }
                const email = undefinedIfEmpty(this.emailAddressElement?.value) ?? undefinedIfEmpty(accountInfo.email) ?? undefinedIfEmpty(getFromLocalStorage('email'));
                if (isDefined(email)) {
                    logEventWithKlayvio(KlayvioEvents.MapCreated, email.toLowerCase(), [
                        { key: KlavioEventPropertyKey.MapName, value: topicData.topic?.name ?? '' },
                        { key: KlavioEventPropertyKey.MapIntentType, value: topicData.getIntentTitle(this.selectedIntent ?? IntentType.Map) },
                        { key: KlavioEventPropertyKey.MapLocation, value: this.suggestedMapCenter?.search ?? '' },
                    ]);
                }
                this.onboardingStep = OnboardingState.Category;
            } else if (this.onboardingStep === OnboardingState.Category) {
                await this.goToSuggestedPoints();
                this.onboardingStep = OnboardingState.Points;
            } else if (this.onboardingStep === OnboardingState.Points) {
                await this.addPointsToMap();
                this.conditionallyCloseOnboardingModal();
            }

        } catch (e) {
            this.dispatchEvent(new CustomEvent('on-close-onboarding-modal'));
            getMainPage().errorToastElement.showToast(`An error has occurred. Context: Could not set up map. Try again later. Error: ${e instanceof Error ? e.message : String(e)}`);
            this.loadingPointSuggestions = false;
            this.loadingPointsToMap = false;
            this.closeOnboardingModal();
        }
    }

    get subHeader(): string {
        if (this.onboardingStep === OnboardingState.Intent) {
            return 'Choose the type of experience you want to create';
        } else if (this.onboardingStep === OnboardingState.Prompt) {
            return `We will help you get going and provide some inspiration. `;
        } else if (this.onboardingStep === OnboardingState.Category) {
            return `Don't stress! You can edit or change these later.`;
        } else if (this.onboardingStep === OnboardingState.Points) {
            return `Keep the points that you want to add to your map or go on to add your own.`;
        } else if (this.onboardingStep === OnboardingState.Subscribe) {
            return '';
        } else if (this.onboardingStep === OnboardingState.Upgrade) {
            return '';
        } else if (this.onboardingStep === OnboardingState.Inspiration) {
            return 'We found these maps for you. Click on one to open it.';
        }

        return 'This is where your map center will be. You can change this later.';
    }

    get header(): string {
        if (this.onboardingStep === OnboardingState.Intent) {
            return 'What are you building today?';
        } else if (this.onboardingStep === OnboardingState.Prompt) {
            return this.isTemplate ? `Let's customize your ${topicData.topic?.name ?? ''}. Where should we center your map?` :`Tell us what you are here to create.`;
        } else if (this.onboardingStep === OnboardingState.Category) {
            return `Categories in Proxi help you organize the places on your map. Here are some ideas.`;
        } else if (this.onboardingStep === OnboardingState.Subscribe) {
            return 'Start a Subscription to Power Your Map';
        } else if (this.onboardingStep === OnboardingState.Upgrade) {
            return 'Upgrade to Get the Most Out of Proxi';
        } else if (this.onboardingStep === OnboardingState.Inspiration) {
            return 'Get Inspired by Other Proxi Maps';
        } else if (this.onboardingStep === OnboardingState.Points) {
            return 'We found some example places for you!';
        }
        return '';
    }

    get footerExitText(): string {
        if (this.onboardingStep === OnboardingState.Intent) {
            return 'Skip Set Up';
        } else if (this.onboardingStep === OnboardingState.Prompt) {
            return 'Skip Set Up';
        } else if (this.onboardingStep === OnboardingState.Category) {
            return 'Take me to my map';
        } else if (this.onboardingStep === OnboardingState.Points) {
            return `I'll add my own places`;
        } else if (this.onboardingStep === OnboardingState.Subscribe) {
            return 'Try a Limited, Free Version of Proxi';
        } else if (this.onboardingStep === OnboardingState.Upgrade) {
            return 'Try a Limited, Free Version of Proxi';
        } else if (this.onboardingStep === OnboardingState.Inspiration) {
            return '';
        }
        return 'I don\'t want suggested points';
    }

    get footerContinueText(): string {
        if (this.onboardingStep === OnboardingState.Intent) {
            return 'Continue';
        } else if (this.onboardingStep === OnboardingState.Prompt) {
            return 'Continue with Setup';
        } else if (this.onboardingStep === OnboardingState.Category) {
            return `Suggest points for my ${this.isTemplate ? 'template':'map'}`;
        } else if (this.onboardingStep === OnboardingState.Points) {
            return 'Add Points to Map';
        } else if (this.onboardingStep === OnboardingState.Subscribe) {
            return 'Start a Proxi Pro Trial Now';
        } else if (this.onboardingStep === OnboardingState.Upgrade) {
            return 'Start a Proxi Pro Trial Now';
        } else if (this.onboardingStep === OnboardingState.Inspiration) {
            return 'Back to Building My Map';
        }
        return 'Continue to Suggested Points';
    }

    get leftSideImage(): string {
        if (this.onboardingStep === OnboardingState.Prompt) {
            return '/images/onboardingsteps/step1.png';
        } else if (this.onboardingStep === OnboardingState.Category) {
            return '/images/onboardingsteps/step2.png';
        } else if (this.onboardingStep === OnboardingState.Points) {
            return '/images/onboardingsteps/step3.png';
        } else if (this.onboardingStep === OnboardingState.Subscribe) {
            return '/images/onboardingsteps/step4.png';
        } else if (this.onboardingStep === OnboardingState.Upgrade) {
            return '/images/onboardingsteps/step4.png';
        } else if (this.onboardingStep === OnboardingState.Inspiration) {
            return '/images/onboardingsteps/step3.png';
        }
        return '/images/onboardingsteps/step1.png';
    }

    getButtonUrl(data: string) {
        return `/images/${this.getDataUrl(data)}.jpg`;
    }

    getDataUrl(data: string) {
        return `onboarding/${dasherize(this.city ?? data)}/${dasherize(data)}`;
    }

    renderPrompt() {
        const intent_title = isDefined(this.selectedIntent) ? topicData.getIntentTitle(this.selectedIntent) : 'map';
        return html`
            <div class="onboarding-prompt-imputs">
                ${this.isTemplate ? '' :
                    html`
                        <proxi-textfield
                            outlined
                            id="gptInitializePrompt"
                            class="input"
                            style="margin-bottom: 1rem"
                            label=${`Title for your ${String(intent_title ?? '')}`}
                            @click=${this.handlePromptFocus}
                            helper=${`e.g. ${this.placeholder}`}
                            helperPersistent=${true}
                            required>
                        </proxi-textfield>
                    `}
                <proxi-input-place
                    id="focusArea"
                    class="input"
                    label="Search for a Focus Area (e.g. Seattle, WA, USA)"
                    @value-changed="${async (e: CustomEvent) => await this.updateSuggestedMapCenter(e.detail.value, e.detail.viewport)}"
                    >
                </proxi-input-place>

                ${isDefined(accountInfo.email) && accountInfo.email !== '' ? '' : html`
                    <proxi-textfield
                        outlined
                        id="emailAddress"
                        class="input"
                        label="Email Address"
                        value="${accountInfo.email ?? getFromLocalStorage('email') ?? ''}"
                        placeholder="e.g. geographer@proxi.co"
                        required>
                    </proxi-textfield>`
                }
                ${this.shouldShowWalkthrough() && isDefined(this.selectedIntent) ? html`
                    <div class="walkthrough-section">
                        <div class="walkthrough-icon">💡</div>
                        <div class="walkthrough-content">
                            <h4 class="walkthrough-title">Want the Best Experience?</h4>
                            <p class="walkthrough-description">
                                For ${topicData.getIntentTitle(this.selectedIntent)}, we recommend learning more about the features to get the most out of your map.
                            </p>
                            <div class="walkthrough-links">
                                <a
                                    href="${topicData.getSetupVideoLink(this.selectedIntent)}"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    class="walkthrough-link">
                                    🎥 Watch Setup Video 🔗
                                </a>
                                <span class="walkthrough-separator">or</span>
                                <a
                                    href="https://app.proxi.co/demo"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    class="walkthrough-link">
                                    📞 Schedule a Demo Walkthrough 🔗
                                </a>
                            </div>
                        </div>
                    </div>
                ` : ''}
            </div>
        `;
    }

    renderIntent() {
        const intents = [
            IntentType.Map,
            IntentType.Guide,
            IntentType.Passport,
            IntentType.Hunt,
            IntentType.Event,
            IntentType.BestOf,
        ];

        return html`
            <div class="intent-container">
                ${intents.map(intent => html`
                    <div
                        class="intent-card ${this.selectedIntent === intent ? 'selected' : ''}"
                        @click="${() => this.selectIntent(intent)}">
                        <div class="intent-icon">${topicData.getIntentIcon(intent)}</div>
                        <div class="intent-content">
                            <h3 class="intent-title">${topicData.getIntentTitle(intent)}</h3>
                            <p class="intent-description">${topicData.getIntentDescription(intent)}</p>
                        </div>
                    </div>
                `)}
            </div>
        `;
    }

    renderCategories() {
        return html`
            <div class="suggested-categories">
                ${topicData.categories.map(category => html`
                    <div class="suggested-category" style="display:flex; flex-direction: row">
                        <div id="iconMenuHolder" class="icon-select">
                            <img src="${category.icon}" style="height: 30px; width: 30px; margin-right: 10px" />
                            <!-- <mwc-icon class="mini-icon-edit">edit</mwc-icon> -->
                        </div>
                        ${this.iconSearchCategory === category ? html`
                            <div class="icon-section">
                                <mwc-icon @click="${()=> {this.suggestedIcons = []; this.iconSearchCategory = undefined;}}">close</mwc-icon>
                                <span class="icon-header">Select New Icon</span>
                                <div class="suggested-icons">
                                    ${this.suggestedIcons.length > 0 ? html`
                                        ${this.suggestedIcons.map(icon => html`
                                            <img
                                                src="https://img.icons8.com/${icon.platform}/${icon.commonName}.png"
                                                @click="${async () => await this.updateIcon(icon)}"
                                                width="24"
                                                height="24"/>
                                        `)}
                                    ` : html`
                                        <div class="icon-header">No Icons Found</div>
                                    `}
                                </div>
                            </div>
                        ` : html `
                            <proxi-textfield
                                outlined
                                class="category-input"
                                style="width:175px; height:2rem;flex:1"
                                @change="${async (e: any) => await this.updateCategoryName(category, e.target.value)}"
                                value="${category.name}"
                                >
                            </proxi-textfield>
                        `}
                        <mwc-icon-button
                            icon="delete"
                            @click="${async () => await this.removeCategory(category._id)}">
                        </mwc-icon-button>
                    </div>
                `)}
                <a @click=${this.addCategory} class="add-category">
                    <mwc-icon>add</mwc-icon>
                    Add Category
                </a>
            </div>
        `;
    }

    renderPoints() {
        return html`
            <div class="suggested-points">
                ${this.suggestedPoints.map(point => {
                    return html`
                        <div class="suggested-category" style="display:flex; flex-direction: row">
                            <span style="flex:1; display:flex; flex-direction:column">
                                <p class="point-name">
                                    <span style="flex:1; overflow:hidden; text-overflow:elipsis">${point.name}</span>
                                    <span class="tag__item"
                                        style="background-color: #fff; border: 2px solid var(--main-primary-color);color: var(--main-primary-color)">
                                        <img style="height: 16px; width: 16px; margin-right: 5px" src="${ifDefined(topicData.categories.find(c => c._id?.$oid === point.topic_category_id.$oid)?.icon)}" />
                                        ${topicData.categories.find(c => c._id?.$oid === point.topic_category_id.$oid)?.name}
                                    </span>
                                    <mwc-icon-button
                                        icon="delete"
                                        style="height:24px; --mdc-icon-button-size: 24px;"
                                        @click="${async () => await this.deleteSuggestedPoint(point)}">
                                    </mwc-icon-button>
                                </p>
                                <p class="point-description">${point.description}</p>
                            </span>
                        </div>

                    `;
                })}
            </div>
        `;
    }

    renderSubscribe() {
        return html`
            <div class="subscribe-container">
                <upgrade-dialog
                    .showHeader=${false}
                    @close="${async () => {this.closeOnboardingModal(false);}}">
                </upgrade-dialog>
            </div>
        `;
    }

    renderUpgrade() {
        return html`
            <div class="subscribe-container">
                <upgrade-dialog
                    .showHeader=${false}
                    @close="${async () => this.closeOnboardingModal(false)}">
                </upgrade-dialog>
            </div>
        `;
    }

    renderContent() {
        switch(this.onboardingStep) {
            case OnboardingState.Intent:
                return this.renderIntent();
            case OnboardingState.Prompt:
                return this.renderPrompt();
            case OnboardingState.Category:
                return this.renderCategories();
            case OnboardingState.Points:
                return this.renderPoints();
            case OnboardingState.Subscribe:
                return this.renderSubscribe();
            case OnboardingState.Upgrade:
                return this.renderUpgrade();
            default:
        }
    }

    renderFooter() {
        return html`
            <div class="bottom-buttons">
                ${(this.onboardingStep === OnboardingState.Intent) || (this.onboardingStep === OnboardingState.Prompt && accountInfo.isAuthenticated)  ? html`
                    <a @click=${this.conditionallyCloseOnboardingModal} class="blank-map">${this.footerExitText}</a>
                ` : html`<div></div>`}
                <div class="right-content" style="display:flex; flex-direction: row; gap: 5px;">
                    ${this.onboardingStep === OnboardingState.Subscribe || this.onboardingStep === OnboardingState.Upgrade ? '' :
                    html`
                    ${this.onboardingStep === OnboardingState.Points ? html`
                        <form-button
                            size="md" class="button save"
                            variant="outline"
                            @click="${this.conditionallyCloseOnboardingModal}">
                            I'll add my own places</form-button>
                    ` : ''}
                        <form-button
                            size="md" class="button save"
                            @click="${this.goToNextStep}">
                                ${this.footerContinueText}
                        </form-button>
                    `}
                </div>
            </div>
            `
        ;
    }


    render() {
        return html`
            ${this.waitingForGPT ?
                html`<gpt-waiting-state
                        .city=${this.city}
                        .gptQuery=${this.gptQuery}>
                    </gpt-waiting-state>`
            : this.categorySuggestionsLoading ?
                html`<loading-element
                        loadingString="Getting You Started">
                    </loading-element>`
            : this.loadingPointSuggestions ?
                html`<loading-element
                        loadingString="Finding Example Points for Your Map">
                    </loading-element>`
            : this.loadingPointsToMap ? html`
                <loading-element
                    loadingString="Adding Example Points to Map">
                </loading-element>
            `: html`
                <div class="modal-container">
                    <div class="flex-container">
                        <div class="right-side-content">
                            <div class="content-area">
                                ${detectMobileViewport() ? '' : html`<h1>${this.header}</h1>`}
                                ${this.onboardingStep !== OnboardingState.Prompt ? html`
                                    <div class="subHeader">
                                    <p>${this.subHeader}</p>
                                    </div>
                                ` : ''}
                                ${this.renderContent()}
                            </div>
                            ${this.renderFooter()}
                        </div>
                    </div>
                </div>`
            }
            <error-toast id="errorToast"></error-toast>
        `;
    }

    private closeOnboardingModal(showChameleon: boolean = true) {
        this.dispatchEvent(new CustomEvent('on-close-onboarding-modal', {detail: {showChameleon}}));
    }

    private conditionallyCloseOnboardingModal() {
        // Comment out SUBSCRIBE on onboarding for now as a test
        // if (this.onboardingStep === OnboardingState.Subscribe || this.onboardingStep === OnboardingState.Upgrade) {
        //     this.closeOnboardingModal();
        // } else if (accountInfo.subscription_customer_id === undefined) {
        //     this.onboardingStep = OnboardingState.Subscribe;
        // } else {
            this.closeOnboardingModal();
        // }
    }

    private async initializeAiTopic(prompt: string) {
        this.categorySuggestionsLoading = true;
        try {
            await getMainPage().errorToastElement.tryOrErrorToast(async () => {
                let fullPrompt = prompt;
                if (this.suggestedMapCenter && typeof this.suggestedMapCenter.search === 'string') {
                    fullPrompt = `${prompt} centered on ${this.suggestedMapCenter.search}`;
                }

                // create topic to get details then delete that detailed topic
                const topic_details = await promptToInitializedMapAI(this.amplitude_callsite, fullPrompt, this.email, this.suggestedMapCenter?.search ?? '', topicData.topicId, topicData.topicKey);

                // create categories
                const categories: TopicCategory[] = [];

                if (isDefined(topic_details.new_categories)) {
                    // add to categories
                    for (const category of topic_details.new_categories) {
                        const icon = await getFirstIcon(this.amplitude_callsite, category);
                        categories.push({
                            name: category,
                            icon: icon ?? getRandomColor(),
                            _id: undefined,
                            icon_size: undefined,
                            can_crowdsource: true,
                        });
                    }
                }

                const map_center: Location = {
                    search: this.suggestedMapCenter?.search ?? topic_details.suggested_map_focus_area,
                    place_id: this.suggestedMapCenter?.place_id ?? '',
                    latLng: this.suggestedMapCenter?.latLng ?? {lat: 0, lng: 0}
                };

                const zoom = pointCache.map?.getZoom() ?? topic_details.suggested_map_zoom_level;

                const changes: Partial<CreateTopicRequest> = {
                    categories,
                    description: topic_details.suggested_map_description,
                    name: topic_details.suggested_map_name,
                    creator: organizationInfo.id ? topicData.topic?.creator : '',
                    header_logo: organizationInfo.id ? topicData.topic?.header_logo : undefined,
                    header_color: organizationInfo.id ? topicData.topic?.header_color : '#bbe2c6',
                    header_text_color: organizationInfo.id ? topicData.topic?.header_text_color : undefined,
                    discovery_level: (accountInfo.legacy_account === true || subscriptionInfo.subscription_id) ? DiscoveryLevel.Private : DiscoveryLevel.ReadPublic,
                    email: this.email ?? accountInfo.email ?? localStorage.getItem('email') ?? undefined,
                    default_map_center: this.viewport && this.newCenter ? map_center : topicData.topic?.default_map_center,
                    default_map_zoom: Math.round(zoom),
                };

                const updated_topic = await topicData.fetchAndUpdateTopicData(changes, this.amplitude_callsite);
                await topicData.instantiateTopicData(this.amplitude_callsite, accountInfo.isAuthenticated, false, false, updated_topic._id.$oid, topicData.topicKey, false, topicData.mapComponent);
                this.requestUpdate();
            }, 'An error has occurred. Context: the server rejected the request due to validation issues.');
        } catch (e) {
            console.error(e);
        }
        this.categorySuggestionsLoading = false;
    }

    private async getUserGeneralLocation() {
        this.userLocation = await getDeviceGeolocation() ?? '';
        if (this.userLocation === '') {
            getMainPage().errorToastElement.showToast('An error has occurred. Context: Could not get your location. Try again later.');
        }
        await delay(2000);
        await this.centerVerification(this.userLocation);
    }


    async updateEmail() {
        const email = undefinedIfEmpty(this.emailAddressElement?.value);

        if (email === undefined) {
            getMainPage().errorToastElement.showToastAndThrow(
                'An error has occurred. Context: The email must be provided'
            );
        }
        if (!validateEmail(email)) {
            getMainPage().errorToastElement.showToast('Please input valid email addresses.');
        }
        await getMainPage().errorToastElement.tryOrErrorToast(async () => {
            if (topicData.topicId === '' || topicData.topicKey === '') {
                getMainPage().errorToastElement.showToastAndThrow(
                    'An error has occurred. Context: The topic cannot be updated: it is invalid.'
                );
                return;
            }
            saveEmail(email ?? '');
        }, 'An error has occurred. Context: Could not add email to map. Try again later.');
    }

    static get styles() {
        return css`

            :host {
                --mdc-menu-max-height: 300px;
                --mdc-menu-min-width: 60px;
                --mdc-icon-button-size: 24px;
            }

            .input {
                margin-left: 5px;
                width: calc(100% - 10px);
                --mdc-text-field-outlined-idle-border-color: var(--main-success-color);
                --mdc-notched-outline-border-color: var(--main-success-color);
            }

            .intent-container {
                display: grid;
                grid-template-columns: repeat(3, 1fr);
                grid-template-rows: repeat(2, auto);
                gap: 1rem;
                width: 100%;
                padding: 1rem 0;
                max-height: 60vh;
                overflow-y: auto;
            }

            .intent-card {
                display: flex;
                flex-direction: column;
                align-items: center;
                padding: 1rem;
                border: 2px solid var(--theme-border-color);
                border-radius: 12px;
                cursor: pointer;
                transition: all 0.3s ease;
                background-color: var(--theme-white-color);
            }

            .intent-card:hover {
                border-color: var(--main-primary-color);
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
                transform: translateY(-2px);
            }

            .intent-card.selected {
                border-color: var(--main-success-color);
                background-color: var(--theme-lightest-color);
                box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
            }

            .intent-icon {
                font-size: 1rem;
            }

            .intent-content {
                text-align: center;
                width: 100%;
            }

            .intent-title {
                font-size: 1rem;
                font-weight: 600;
                line-height: 1;
                margin: 0 0 0.5rem 0;
                color: var(--theme-input-color);
            }

            .intent-description {
                font-size: 0.75rem;
                line-height: 1;
                color: var(--theme-input-color);
                margin: 0;
                opacity: 0.8;
                text-align: center;
            }

            .intent-container::-webkit-scrollbar {
                width: 5px;
            }

            .intent-container::-webkit-scrollbar-track {
                background: var(--theme-light-scrollbar-color);
            }

            .intent-container::-webkit-scrollbar-thumb {
                background: var(--main-moon-color);
            }

            .walkthrough-section {
                display: flex;
                align-items: flex-start;
                gap: 1rem;
                padding: 1rem;
                margin-top: 1rem;
                background: linear-gradient(135deg, #f8f9ff 0%, #fff8f0 100%);
                border: 2px solid var(--main-primary-color);
                border-radius: 12px;
                box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
            }

            .walkthrough-icon {
                font-size: 2rem;
                flex-shrink: 0;
            }

            .walkthrough-content {
                flex: 1;
                display: flex;
                flex-direction: column;
                gap: 0.25rem;
            }

            .walkthrough-title {
                font-size: 0.9rem;
                font-weight: 600;
                margin: 0;
                color: var(--theme-input-color);
            }

            .walkthrough-description {
                font-size: 0.7rem;
                margin: 0;
                color: var(--theme-input-color);
                opacity: 0.85;
            }

            .walkthrough-links {
                display: flex;
                align-items: center;
                gap: 0.75rem;
                flex-wrap: wrap;
                margin-top: 0.5rem;
            }

            .walkthrough-separator {
                font-size: 0.875rem;
                color: var(--theme-input-color);
                opacity: 0.6;
                font-weight: 500;
            }

            .walkthrough-link {
                display: inline-flex;
                align-items: center;
                gap: 0.25rem;
                color: var(--main-primary-color);
                font-weight: 600;
                font-size: 0.875rem;
                text-decoration: none;
                transition: all 0.2s ease;
            }

            .walkthrough-link:hover {
                color: var(--main-success-color);
                text-decoration: underline;
            }

            .suggested-maps{
                display: flex;
                flex-direction: row;
                flex-wrap: wrap;
                gap: 10px;
                justify-content: center;
                overflow-y: auto;
                max-height: 500px;
            }

            .onboarding-prompt-imputs {
                display: flex;
                flex-direction: column;
                gap: 20px;
                width: 100%;
                padding-top: 10px;
                min-height: 40vh;
            }

            .input:invalid {
                box-shadow: unset;
            }

            .blank-map {
                cursor: pointer;
                text-decoration: underline;
            }

            .suggested-icons {
                display: flex;
                flex-direction: row;
                align-items: flex-start;
                flex:1;
                max-width: 300px;
                overflow-x: scroll;
                gap: 5px;
                cursor: pointer;
            }

            .suggested-icons::-webkit-scrollbar {
                display: none;
            }

            p {
                text-align: left;
                font-size: 1rem;
                font-style: normal;
                font-weight: 500;
                line-height: 120%;
                margin-top: 0;
            }

            .icon-select {
                display: flex;
                flex-direction: row;
                align-items: center;
            }


            .modal-container {
                flex-direction: column;
                align-items: flex-start;
                min-width: 50vw;
            }

            h1 {
                font-size: 1.5rem;
                margin: 12px 0px;
                text-align: left;
            }

            paper-input-place {
                width: 100%;
                margin-left: 0;
            }

            .suggested-cities {
                display: flex;
            }

            .suggested-points {
                display: flex;
                flex-direction: column;
                flex: 1;
                overflow-y: auto;
                width: 100%;
            }

            proxi-textfield {
                cursor: text;
            }

            /* width */
            .suggested-points::-webkit-scrollbar {
                width: 5px;
            }
            /* Track */
            .suggested-points::-webkit-scrollbar-track {
                background: var(--theme-light-scrollbar-color);
            }
            /* Handle */
            .suggested-points::-webkit-scrollbar-thumb {
                background: var(--main-moon-color);
            }

            .tag__item {
                position: relative;
                user-select: none;
                cursor: pointer;
                display: flex;
                justify-content: center;
                align-items: center;
                padding: 0px 10px;
                border-radius: 1.25rem;
                margin-right: 6px;
                margin-bottom: 4px;
                font-size: 10px;
                border: 1px solid var(--main-primary-color);
                --mwc-icon-size: 12px;
            }

            .tag__icon {
                border-radius: 999px;
                width: 1rem;
                height: 1rem;
                font-size: 16px;
                display: flex;
                align-items: center;
                justify-content: center;
                position: absolute;
                top: -5px;
                right: -5px;
                background: var(--theme-white-color);
                border: 1px solid var(--main-primary-color);
                color: var(--main-primary-color);
            }

            .top-level-subtags-container {
                background-color: transparent;
                display: flex;
                flex-wrap: wrap;
                flex:1;
                display:flex;
            }

            .add-category {
                display: flex;
                flex-direction: row;
                align-items: center;
                justify-content: center;
                cursor: pointer;
            }

            .go-button {
                padding-top: 8px;
                background-color: var(--main-success-color);
            }

            .gpt-toggle-wrapper {
                display: flex;
                justify-content: center;
                margin: 12px;
                color: var(--theme-input-color);
            }

            .toggleSection {
                display: flex;
                flex-direction: row;
                align-items: center;
                justify-content: center;
            }

            .point-name {
                font-weight: 600;
                font-size: 1rem;
                margin: 0;
                text-align: left;
                display: flex;
                flex-direction: row;
                align-items: center;
                justify-content: space-between;
            }

            .point-description {
                font-size: 0.6rem;
                margin: 0;
                text-align: left;
            }

            .bottom-buttons {
                display: flex;
                flex-direction: row;
                align-items: center;
                justify-content: space-between;
                margin-top: 2rem;
            }

            .bottom-buttons.small {
                justify-content: center;
                margin-top: 0;
                gap: 1rem;
            }

            .suggested-category {
                margin: 0.25rem;
            }

            .gpt-toggle {
                padding-right: 4px;
                padding-left: 6px;
            }

            .gpt-input {
                margin: 0 8px;
            }

            .mini-icon-edit {
                position: relative;
                --mdc-icon-size: 15px;
                margin-left: -10px;
                margin-right: 10px;
            }


            .centered-in-text {
                margin-left: 10px;
                margin-top: 5px;
                font-size: 0.8rem;
            }

            .change-center-text {
                margin-left: 10px;
                text-decoration: underline;
                cursor: pointer;
                color: var(--main-primary-color);
            }

            .suggested-item {
                border: 0px;
                font-weight: 600;
                cursor: pointer;
                background-size: cover;
                background-repeat: no-repeat;
                display: flex;
                align-items: flex-end;
                width: 150px;
                border-radius: 16px;
                height: 100px;
                padding: 0px;
                margin: 4px;
                box-shadow: 3px 3px whitesmoke;
            }

            .suggested-categories {
                display: flex;
                flex-direction: column;
                flex-wrap: wrap;
                max-height: 200px;
                column-gap: 1rem;
                overflow: auto;
                width:100%;
            }

            .suggested-categories::-webkit-scrollbar {
                width: 5px;
            }

            .suggested-categories::-webkit-scrollbar-track {
                background: var(--theme-light-scrollbar-color);
            }

            .suggested-categories::-webkit-scrollbar-thumb {
                background: var(--main-moon-color);
            }

            .back-to-backpack {
                position: absolute;
                top: 0;
                left: 0;
                text-decoration: underline;
                z-index: 1;
                background-color: white;
                padding: 3px;
                border-radius: 3px;
                font-size: 0.7rem;
                --mdc-icon-size: 12px;
                max-width: 100px;
                line-height: 1.5;
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                gap: 5px;
                width: 10%;
                cursor: pointer;
            }

            .suggested-categories::-webkit-scrollbar {
                width: 5px;
            }

            .suggested-categories::-webkit-scrollbar-track {
                background: var(--theme-light-scrollbar-color);
            }

            .suggested-categories::-webkit-scrollbar-thumb {
                background: var(--main-moon-color);
            }

            .icon-section {
                display: flex;
                flex-direction: row;
                align-items: center;
                justify-content: center;
                width: 95%;
                gap: 1rem;
                border: 1px solid var(--theme-light-border-color);
                border-radius: 4px;
                background-color: var(--theme-lightest-color);
                height: 2rem;
            }

            .suggested-item--no-image {
                border: 0px;
                background: white;
                padding: 0 4px;
                margin: 4px;
                font-weight: 600;
                cursor: pointer;
                width: 150px;
                border-radius: 16px;
                box-shadow: 0px 2px 8px -4px rgb(31 27 45 / 12%), 0px 4px 16px rgb(31 27 45 / 12%);
            }

            .suggested-categories mwc-select {
                position: relative;
                z-index: 1000;
            }

            .suggested-item-name-container {
                color: black;
                background: white;
                width: 100%;
                height: 35px;
                border-bottom-right-radius: 16px;
                border-bottom-left-radius: 16px;
                padding-bottom: 6px;
            }

            .spacing {
                margin-top: 1rem;
            }

            .suggestion-holder {
                display: flex;
                flex-wrap:wrap;
                justify-content: left;
                margin-bottom: 1rem;
                overflow:auto
            }

            .suggested-item-name {
                vertical-align: -webkit-baseline-middle;
                line-height: 1px;
            }

            #googleInputDiv {
                display: flex;
                margin-bottom: 15px;
                margin-top: 10px;
                width: 100%;
                align-items: center;
            }

            .hidden {
                display: none;
            }

            .subHeader {
                display: flex;
                justify-content: left;
                font-size: small;
                text-align: left;
                color: var(--theme-input-color);
                margin-top:0;
            }

            .link {
                display: flex;
                margin-top: 10px;
                margin-bottom:10px;
                justify-content: center;
            }

            .learnLink {
                color: blue;
                text-decoration: underline;
                cursor: pointer;
                font-size: small;
            }

            loading-element {
                margin: 1rem;
            }
            .subscribe-container {
                display: flex;
                flex-direction: column;
                align-items: center;
                flex: 1 1 0%;
                overflow-y: auto;
            }

            .billing-toggle-container {
                display: flex;
                justify-content: center;
                align-items: center;
                margin-bottom: 20px;
            }

            .billing-toggle-label {
                margin: 0 10px;
            }

            .tiers-container {
                display: flex;
                justify-content: space-between;
                width: 100%;
            }

            .tier {
                display: flex;
                flex-direction: column;
                align-items: center;
                border: 1px solid #ccc;
                border-radius: 10px;
                padding: 20px;
                width: 45%; /* Adjust this value to fit your design */
            }

            .tier-header {
                margin-bottom: 20px;
            }

            .tier-body {
                margin-bottom: 20px;
            }

            .tier-footer {
                width: 100%;
            }

            .flex-container {
                display: flex;
                flex-direction: row;
                gap: 2rem;
                max-height: calc(70vh - 40px);
            }

            .left-side-image {
                width: 10%;
                overflow: hidden;
                display: flex;
                flex-direction: column;
                align-items: center;
                overflow-x: hidden;
                overflow-y: auto;
            }

            .left-side-image img {
                height: 70vh;
            }

            .left-side-image .inspiration {
                overflow-y: auto;
                margin-left:0;
                margin-right:0;
                padding:10px;
            }

            .left-side-image::-webkit-scrollbar {
                width: 5px;
            }
            /* Track */
            .left-side-image::-webkit-scrollbar-track {
                background: var(--theme-light-scrollbar-color);
            }
            /* Handle */
            .left-side-image::-webkit-scrollbar-thumb {
                background: var(--main-moon-color);
            }

            .get-inspired-text {
                margin-bottom: 0;
                background-color: white;
                width: 80%;
                text-align: center;
                border-radius: 3px;
            }

            .right-side-content {
                flex: 1;
                display: flex;
                flex-direction: column;
                justify-content: space-between;
                width: 40vw;
                margin: 0 1rem;
            }

            .content-area {
                flex: 1;
                overflow-y: auto;
                overflow-x: hidden;
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                justify-content: flex-start;
            }

            @media (max-width: 550px) {
                .gpt-toggle-wrapper {
                    line-height: 1;
                }

                .right-side-content {
                    padding: 0;
                    margin: 0;
                    display: flex;
                }

                .flex-container {
                    height: calc(var(--slotted-content-height) - 20px);
                    max-height: unset;
                }

                .suggested-categories {
                    flex-wrap: nowrap;
                    flex: 1;
                    max-width: 95vw;
                }

                .bottom-buttons {
                    margin-top: 1rem;
                }

                .intent-container {
                    grid-template-columns: 1fr;
                    grid-template-rows: auto;
                }
            }
        `;
    }
}